import { parseOssVariant } from './ossVariant.js'

/*
 * The quarterly OSS return and the EUR 10,000 threshold, from figures the
 * ledger and the invoices already aggregate. Pure: the VAT return page fetches
 * the rows and the ECB rate, this does the arithmetic.
 *
 * Currency (VAT Directive art. 369h): the return is in EUR. A sale invoiced in
 * EUR is reported as invoiced; any other is converted at the ECB rate of the
 * period's last day (or the next day the ECB publishes one). `eurRate` is that
 * rate as the ECB quotes it — units of the base currency per EUR (DKK 7.46).
 * A sale invoiced in a third currency (neither EUR nor the base currency) is
 * converted from its base-currency amount — close, not exact.
 */

export const EU_COUNTRIES = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU',
    'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK']

export const OSS_THRESHOLD_EUR = 10000

const round2 = (v) => Math.round(v * 100) / 100
const num = (v) => Number(v) || 0

/*
 * `rows`: ledgerEntry/agr/byOssCode — the OSS net legs per tax code and
 * currency: { taxcodeid, transactioncurrency, base, vat, net, entries }.
 * Sales post as credits (negative); a credit note's reversal as debits, so it
 * reduces the figures. Rows whose code is not a country-rate OSS code (DK's
 * fixed 25 % OSS codes are Danish VAT) are not part of the return.
 *
 * Returns { lines: [{ country, rate, taxable, vat, entries }], byCountry:
 * [{ country, taxable, vat }], total: { taxable, vat }, needsRate }.
 * `needsRate` says EUR figures could not be computed for want of `eurRate`.
 */
export const ossReturn = (rows = [], { baseCurrency, eurRate } = {}) => {
    const groups = new Map()
    let needsRate = false

    for (const row of rows) {
        const code = parseOssVariant(row.taxcodeid ?? row.taxCodeId)
        if (!code) continue
        const currency = row.transactioncurrency ?? row.transactionCurrency
        let taxable, vat
        if (currency === 'EUR') {
            taxable = -num(row.net)
            vat = taxable * code.rate
        } else if (baseCurrency === 'EUR') {
            taxable = -num(row.base)
            vat = -num(row.vat)
        } else if (eurRate > 0) {
            taxable = -num(row.base) / eurRate
            vat = -num(row.vat) / eurRate
        } else {
            needsRate = true
            continue
        }
        const key = `${code.country}|${code.rate}`
        const g = groups.get(key) ?? { country: code.country, rate: code.rate, taxable: 0, vat: 0, entries: 0 }
        g.taxable += taxable
        g.vat += vat
        g.entries += num(row.entries)
        groups.set(key, g)
    }

    const lines = [...groups.values()]
        .map(g => ({ ...g, taxable: round2(g.taxable), vat: round2(g.vat) }))
        .sort((a, b) => a.country.localeCompare(b.country) || b.rate - a.rate)
    const byCountry = Object.values(lines.reduce((acc, l) => {
        const c = (acc[l.country] ??= { country: l.country, taxable: 0, vat: 0 })
        c.taxable = round2(c.taxable + l.taxable)
        c.vat = round2(c.vat + l.vat)
        return acc
    }, {}))
    const total = {
        taxable: round2(lines.reduce((t, l) => t + l.taxable, 0)),
        vat: round2(lines.reduce((t, l) => t + l.vat, 0))
    }
    return { lines, byCountry, total, needsRate }
}

/*
 * Sales to private customers in other EU countries over a period, in EUR,
 * against the EUR 10,000 a year above which OSS (or registering in each
 * country) is mandatory. From invoice/agr/byCustomerCountry and the same for
 * credit notes: { customercountry, companytypeid, vatidstatus, net } with net
 * in the base currency. A business customer without a validated VAT number
 * counts as private, as it does for the VAT code.
 *
 * Counts every such sale. The threshold itself counts distance sales of goods
 * and telecom, broadcasting and electronic services only, so other services
 * sold to consumers abroad make this an upper bound.
 */
export const ossThreshold = (invoiceRows = [], creditNoteRows = [], { orgCountry, baseCurrency, eurRate } = {}) => {
    const private_ = (r) => {
        const country = r.customercountry ?? r.customerCountry
        const type = r.companytypeid ?? r.companyTypeId
        const status = r.vatidstatus ?? r.vatIdStatus
        return EU_COUNTRIES.includes(country) && country !== orgCountry && !(type === 'company' && status === 'valid')
    }
    const sum = (rows) => rows.filter(private_).reduce((t, r) => t + num(r.net), 0)
    const base = sum(invoiceRows) - sum(creditNoteRows)
    const eur = baseCurrency === 'EUR' ? base : (eurRate > 0 ? base / eurRate : null)
    return {
        base: round2(base),
        eur: eur === null ? null : round2(eur),
        threshold: OSS_THRESHOLD_EUR,
        share: eur === null ? null : eur / OSS_THRESHOLD_EUR,
        exceeded: eur !== null && eur > OSS_THRESHOLD_EUR
    }
}
